import { ApolloClient, gql } from 'apollo-boost';
import { SESEventLog } from './eventUtils';
import { client } from 'shared/apollo';

const query = ({
  toEmail,
  startDate,
  endDate,
  eventType,
  messageId,
  emailType,
}: {
  toEmail?: string;
  startDate?: string;
  endDate?: string;
  eventType?: string;
  messageId?: string;
  emailType?: string;
}) => gql`
  query allSesLogs(
    ${toEmail ? '$toEmail: String' : ''}
    ${startDate ? '$startDate: Date' : ''}
    ${endDate ? '$endDate: Date' : ''}
    ${eventType ? '$eventType: String' : ''}
    ${messageId ? '$messageId: String' : ''}
    ${emailType ? '$emailType: String' : ''}
  ) {
    allSesLogs(
      filters: {
        ${toEmail ? 'toEmail: $toEmail' : ''}
        ${startDate ? 'startDate: $startDate' : ''}
        ${endDate ? 'endDate: $endDate' : ''}
        ${eventType ? 'eventType: $eventType' : ''}
        ${messageId ? 'messageId: $messageId' : ''}
        ${emailType ? 'emailType: $emailType' : ''}
      }
    ) {
      edges {
        node {
          eventType
          source
          timeStamp
          sendingAccountId
          messageId
          destination
          fromEmail
          toEmail
          subject
          body
          emailType
          openTimeStamp
          openIpAddress
          openUserAgent
          deliveryTimeStamp
          deliveryProcessingTimeMillis
          deliveryRecipients
          deliverySmtpResponse
          deliveryReportingMta
        }
      }
    }
  }
`;

export const getSesLogByEmail = (
  client: ApolloClient<object>,
  toEmail?: string,
  startDate?: string, // "2023-02-27"
  endDate?: string, // "2023-03-01"
  eventType?: SESEventLog['eventType'],
  messageId?: string,
  emailType?: string,
) => {
  const variables = {
    toEmail,
    startDate,
    endDate,
    eventType,
    messageId,
    emailType,
  };
  if (
    toEmail === undefined &&
    startDate === undefined &&
    endDate === undefined &&
    eventType === undefined &&
    messageId === undefined &&
    emailType === undefined
  ) {
    const today = new Date().toISOString().split('T')[0];
    return client.query({
      query: query({
        startDate: today,
        endDate: today,
      }),
      variables: {
        startDate: today,
        endDate: today,
      },
      fetchPolicy: 'network-only',
    });
  }
  return client.query({
    query: query(variables),
    variables,
    fetchPolicy: 'network-only',
  });
};

const accountsQuery = gql`
  {
    adminAccounts (filters: { type: "grantee", enabled: true }) {
      edges {
        node {
          dbId
          name
        }
      }
    }
  }
`;

export interface Accounts {
  dbId: number;
  name: string;
}

export const getAccounts = (client: ApolloClient<object>) => {
  return client.query<{
    adminAccounts: Accounts[];
  }>({
    query: accountsQuery,
    fetchPolicy: 'no-cache',
  });
};

const portfoliosQuery = gql`
  {
    adminPortfolios(filters: { enabled: true }) {
      edges {
        node {
          dbId
          name
        }
      }
    }
  }
`;

const portfoliosQueryWithReportId = gql`
  query adminPortfolios($accountId: ID!)
  {
    adminPortfolios(filters: { id: $accountId, enabled: true }) {
      edges {
        node {
          dbId
          name
          reportId
          countReports
          investments {
            edges {
              node {
                allocations {
                  edges {
                    node {
                      report {
                        dbId
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;


export const getPortfoliosById = (
  client: ApolloClient<object>,
  accountId: number
) => {
  return client.query({
    query: portfoliosQueryWithReportId,
    variables: { accountId },
    fetchPolicy: 'network-only',
  });
};

export interface Portfolios {
  dbId: number;
  name: string;
}

export const getPortfolios = (client: ApolloClient<object>) => {
  return client.query<{
    adminPortfolios: Portfolios[];
  }>({
    query: portfoliosQuery,
    fetchPolicy: 'no-cache',
  });
};

const donorsQuery = gql`
  {
    adminAccounts (filters: { type: "funder", enabled: true }) {
      edges {
        node {
          dbId
          name
          type
        }
      }
    }
  }
`;

export interface Donors {
  Id: number;
  name: string;
}

export const getDonorsFilters = (client: ApolloClient<object>) => {
  return client.query<{
    adminAccounts: Donors[];
  }>({
    query: donorsQuery,
    fetchPolicy: 'no-cache',
  });
};

export interface NotificationDelivery {
  sesMessageId: string;
  reportId: number;
  accountId: number;
  portfolioName: string;
  investmentId: number;
}

const allNotificationDeliveries = gql`
  query allNotificationDeliveries {
    allNotificationDeliveries {
      edges {
        node {
          sesMessageId
          reportId
          accountId
          portfolioName
          investmentId
        }
      }
    }
  }
`;

const notificationQuery = ({
  id,
  sesMessageId,
  accountId,
  investmentId,
  reportId,
}: {
  id?: number;
  sesMessageId?: string;
  accountId?: number;
  investmentId?: number;
  reportId?: number;
}) => gql`
  query allNotificationDeliveries(
    ${id ? '$id: Int' : ''}
    ${sesMessageId ? '$sesMessageId: String' : ''}
    ${accountId ? '$accountId: Int' : ''}
    ${investmentId ? '$investmentId: Int' : ''}
    ${reportId ? '$reportId: Int' : ''}
  ) {
    allNotificationDeliveries(
      filters: {
        ${id ? 'id: $id' : ''}
        ${sesMessageId ? 'sesMessageId: $sesMessageId' : ''}
        ${accountId ? 'accountId: $accountId' : ''}
        ${investmentId ? 'investmentId: $investmentId' : ''}
        ${reportId ? 'reportId: $reportId' : ''}
      }
    ) {
      edges {
        node {
          sesMessageId
          reportId
          accountId
          portfolioName
        }
      }
    }
  }
`;

export const getNotificationDeliveriesByFilters = (
  client: ApolloClient<object>,
  id?: number,
  sesMessageId?: string,
  accountId?: number,
  investmentId?: number,
  reportId?: number
) => {
  const variables = {
    id,
    sesMessageId,
    accountId,
    investmentId,
    reportId,
  };
  return client.query({
    query: notificationQuery(variables),
    variables,
    fetchPolicy: 'network-only',
  });
};

export const getAllNotificationDeliveries = (client: ApolloClient<object>) => {
  return client.query({
    query: allNotificationDeliveries,
    fetchPolicy: 'network-only',
  });
};

const reportsQuery = ({
  programId,
  accountId
}: {
  programId?: number;
  accountId?: number;
}) => gql`
  query allReports(
      ${programId ? '$programId: Int' : ''}
      ${accountId ? '$accountId: Int' : ''}
    ) {
    allReports(
      filters: {
        ${programId ? 'programId: $programId' : ''}
        ${accountId ? 'accountId: $accountId' : ''}
      }
      ) {
      edges {
        node {
          reportId
          programId
          accountId
          program {
            name
          }
        }
      }
    }
  }
`;


export const getReports = (
  client: ApolloClient<object>,
  programId?: number,
  accountId?: number,
) => {
  const variables = {
    programId,
    accountId,
  };
  return client.query({
    query: reportsQuery(variables),
    variables: {
      programId,
      accountId,
    },
    fetchPolicy: 'network-only',
  });
};